import * as signalR from "@microsoft/signalr";
import {
  Box,
  Paper,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  ListSubheader,
} from "@mui/material";
import { useEffect, useState, useRef } from "react";
import { useLoaderData, useNavigate, useParams } from "react-router-dom";
import { Question } from "../../exam/type";
import QuestionItem from "../../question/QuestionItem";

export default function UploadStatus() {
  const { examId } = useParams();
  const connection = useLoaderData() as signalR.HubConnection;
  const [logMessages, setLogMessages] = useState<string[]>([]);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [questions, setQuestions] = useState<Question[]>([]);
  const navigate = useNavigate();
  // Create refs for the log containers
  const logContainerRef = useRef<HTMLDivElement>(null);
  const errorContainerRef = useRef<HTMLDivElement>(null);

  // SignalR bağlantısını kur
  useEffect(() => {
    // Update loading state based on connection state
    setIsLoading(connection.state !== signalR.HubConnectionState.Connected);

    // Set up event listeners for log messages
    connection.on("SendLogInfo", (newMessage: string) => {
      if (!isProcessing) setIsProcessing(true);
      setLogMessages((prevMessages) => [...prevMessages, newMessage]);
      // Scroll will be handled in another useEffect
    });

    connection.on("SendAddedQuestion", (questions: Question[]) => {
      setQuestions((q) => [...q, ...questions]);
    });

    connection.on("SendStart", () => {
      setIsProcessing(true);
    });

    connection.on("SendLogCompleted", () => {
      setIsProcessing(false);
      navigate("/exams/" + examId);
    });

    connection.on("SendStop", () => {
      setIsProcessing(false);
    });

    // Clean up event listeners when component unmounts
    return () => {
      connection.off("SendLogInfo");
      connection.off("SendLogError");
      connection.off("SendStart");
      connection.off("SendStop");
      connection.off("SendLogCompleted");
    };
  }, [connection]);

  // Scroll to top whenever logMessages changes
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logMessages]);

  // Scroll to top whenever errorMessages changes
  useEffect(() => {
    if (errorContainerRef.current) {
      errorContainerRef.current.scrollTop =
        errorContainerRef.current.scrollHeight;
    }
  }, [errorMessages]);

  const handleStop = () => {
    connection.invoke("StopProcess", examId).catch((err) => {
      console.error("Error stopping the process:", err);
      setErrorMessages((prev) => [
        ...prev,
        "Failed to stop the process: " + err,
      ]);
    });
  };

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Upload Status Tracker
      </Typography>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Connecting to server...</Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ display: "flex", mb: 2, gap: 2 }}>
            <Button
              variant="contained"
              color="error"
              onClick={handleStop}
              disabled={!isProcessing}
            >
              Süreci Durdur
            </Button>
            {isProcessing && (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                <Typography>Processing...</Typography>
              </Box>
            )}
          </Box>

          <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
            <List
              dense
              sx={{
                width: "100%",
                position: "relative",
                overflow: "auto",
                maxHeight: 600,
                "& ul": { padding: 0 },
              }}
              subheader={<ListSubheader>Loglar</ListSubheader>}
              ref={logContainerRef as any}
            >
              {logMessages.length > 0 ? (
                logMessages.map((message, index) => (
                  <ListItem
                    key={index}
                    divider={index < logMessages.length - 1}
                  >
                    <ListItemText primary={message} />
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText primary="No log messages yet" />
                </ListItem>
              )}
            </List>
          </Paper>

          {errorMessages.length > 0 && (
            <Paper
              elevation={3}
              sx={{ p: 2, bgcolor: "#fff8f8" }}
              ref={errorContainerRef}
            >
              <Typography variant="h6" color="error" gutterBottom>
                Hatalar
              </Typography>
              <List dense>
                {errorMessages.map((error, index) => (
                  <ListItem
                    key={index}
                    divider={index < errorMessages.length - 1}
                  >
                    <ListItemText
                      primary={error}
                      primaryTypographyProps={{ color: "error" }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
          {questions.length > 0 && (
            <Paper
              elevation={3}
              sx={{ p: 2, bgcolor: "#fff8f8" }}
              ref={errorContainerRef}
            >
              <Typography variant="h6" color="error" gutterBottom>
                Sorular
              </Typography>
              <List dense>
                {questions.map((question, index) => (
                  <QuestionItem
                    question={question}
                    key={index}
                    showAnswers
                    showSection
                  />
                ))}
              </List>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
}
